import { StandOperationResult } from '../../entities/standOperationResult'
import { TransportTimeoutException } from '../../errors/transportTimeout'

class V14OperationCancelledException extends Error {
  constructor() {
    super('v14 operation cancelled')
    Object.setPrototypeOf(this, V14OperationCancelledException.prototype)
  }
}

export function isV14OperationCancelledException(error: unknown): boolean {
  return error instanceof V14OperationCancelledException
}

type Deferred<T> = {
  promise: Promise<T>
  resolve: (value: T) => void
  reject: (error: unknown) => void
  isSettled: () => boolean
}

function createDeferred<T>(): Deferred<T> {
  let settled = false
  let resolveFn!: (value: T) => void
  let rejectFn!: (error: unknown) => void
  const promise = new Promise<T>((res, rej) => {
    resolveFn = (v) => {
      settled = true
      res(v)
    }
    rejectFn = (e) => {
      settled = true
      rej(e)
    }
  })
  // cancel 後に誰も await しない場合の unhandled rejection を抑止
  promise.catch(() => {})
  return {
    promise,
    resolve: resolveFn,
    reject: rejectFn,
    isSettled: () => settled,
  }
}

// タイムアウト専用の目印。waitForResult はこの型だけを TransportTimeoutException へ
// 変換し、それ以外の reject（cancel sentinel／repository が繋ぐ実 I/O の本物の error）は
// 素通しさせる。platform Dart の `on TimeoutException`（タイムアウト例外だけを名指しで
// catch）と挙動を一致させ、本物の error が timeout に誤ラベルされるのを防ぐ。
class TimeoutSignal {}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutSignal()), ms)
    promise.then(
      (v) => {
        clearTimeout(timer)
        resolve(v)
      },
      (e) => {
        clearTimeout(timer)
        reject(e)
      }
    )
  })
}

export class V14Operation {
  private readonly ackTimeout: number
  private readonly resultTimeout: number
  private readonly ack = createDeferred<void>()
  private readonly result = createDeferred<StandOperationResult>()

  constructor(
    options: { ackTimeoutMs?: number; resultTimeoutMs?: number } = {}
  ) {
    this.ackTimeout = options.ackTimeoutMs ?? 3000
    this.resultTimeout = options.resultTimeoutMs ?? 15000
  }

  async waitForResult(): Promise<StandOperationResult> {
    try {
      await withTimeout(this.ack.promise, this.ackTimeout)
    } catch (e) {
      if (e instanceof TimeoutSignal) throw new TransportTimeoutException('ack')
      throw e
    }

    try {
      return await withTimeout(this.result.promise, this.resultTimeout)
    } catch (e) {
      if (e instanceof TimeoutSignal) {
        throw new TransportTimeoutException('result')
      }
      throw e
    }
  }

  onAckReceived(): void {
    if (!this.ack.isSettled()) this.ack.resolve()
  }

  onResultReceived(result: StandOperationResult): void {
    if (!this.result.isSettled()) this.result.resolve(result)
  }

  cancel(): void {
    if (!this.ack.isSettled())
      this.ack.reject(new V14OperationCancelledException())
    if (!this.result.isSettled())
      this.result.reject(new V14OperationCancelledException())
  }
}
